import { useMutation } from '@apollo/client/react';
import { kebabCase } from '@fergdigitalcommerce/fergy-utilities';
import { logError } from 'fergy-core-react-logging';
import { type FunctionComponent, type HTMLAttributes, useEffect, useState } from 'react';
import {
	type ReportRecommendationEngagementMutationVariables,
	type ProductOptionFieldsFragment,
	type ProductOptionGroupFieldsFragment,
	type ProductOptionSourceType,
	type ReportRecommendationEngagementMutation
} from '../../../__generated__/graphql-client-types';
import {
	type AddToCartAction,
	isCurrentOptionSelected,
	openProductLinkInNewTab
} from '../../../helpers/product-helper/configuration/product-option.helper';
import { REPORT_RECOMMENDATION_ENGAGEMENT } from '../../../queries/dynamic-yield/dynamic-yield.queries';
import { type OptionSelection } from '../../../types/product-option.types';
import { type ProductResourceVariantCard } from '../../../types/product.types';
import { StyledButton } from '../../buttons/styled-button/styled-button.component';
import { ProductCardActions } from '../product-option-card/product-card-actions/product-card-actions.component';
import { ProductSelectionCard } from '../product-option-card/product-selection-card.component';

const GROUP_SIZE = 8;

/**
 * @typedef ProductOptionRecommendedProps
 */
export type ProductOptionRecommendedProps = {
	label: string;
	readonly?: boolean;
	automationHook?: string;
	addToCart?: AddToCartAction;
	variantName?: string;
	source?: ProductOptionSourceType;
	isAddToCartBusy?: boolean;
} & ProductOptionFieldsFragment &
	Pick<HTMLAttributes<HTMLElement>, 'className'>;

/**
 * Product Option Recommended Component
 *
 * @param {ProductOptionRecommendedProps} option
 * @param {AddToCartMutation} option.addToCart
 * @param {string} [option.className]
 */
export const ProductOptionRecommended: FunctionComponent<ProductOptionRecommendedProps> = ({
	className = 'w-100 pa3',
	addToCart,
	variantName,
	source,
	isAddToCartBusy,
	...option
}) => {
	const { resource, label, slotId } = option;
	const currentVariant =
		resource?.finishes.find((finish) => finish.title === variantName) ||
		resource?.finishes.find((finish) => finish.title.includes(variantName?.split(' ')[variantName.split(' ').length - 1] ?? '')) ||
		resource?.finishes[0];
	const [selectedVariant, setSelectedVariant] = useState<ProductResourceVariantCard | undefined>(currentVariant);
	const [newQuantity, setNewQuantity] = useState<number | undefined>(resource?.quantity || 1);
	const [sendDyRecommendationEngagement] = useMutation<
		ReportRecommendationEngagementMutation,
		ReportRecommendationEngagementMutationVariables
	>(REPORT_RECOMMENDATION_ENGAGEMENT);
	const [isThisTheProductBeingAddedToCart, setIsThisTheProductBeingAddedToCart] = useState<boolean>(false);

	/**
	 * Variant Selected Click
	 */
	const onVariantSelected = (variantSelected: ProductResourceVariantCard) => setSelectedVariant(variantSelected);

	/**
	 * Add To Cart Click Handler
	 */
	const onAddToCartClick = async () => {
		if (selectedVariant && addToCart && newQuantity !== undefined) {
			if (source === 'DYNAMIC_YIELD') {
				sendDyRecommendationEngagement({
					variables: {
						slotId
					}
				}).catch(logError);
			}
			setIsThisTheProductBeingAddedToCart(true);
			await addToCart({ variantId: selectedVariant.id, quantity: newQuantity }, option, selectedVariant, source);
			setIsThisTheProductBeingAddedToCart(false);
		}
	};

	const onConfigureClick = () => {
		openProductLinkInNewTab(selectedVariant?.url ? `${selectedVariant.url}&quantity=${newQuantity}` : '#');
	};

	useEffect(() => {
		if (currentVariant && selectedVariant && currentVariant.id !== selectedVariant.id) {
			setSelectedVariant(currentVariant);
		}
	}, [resource]);

	return selectedVariant ? (
		<ProductSelectionCard
			{...option}
			className={className}
			ElementTag="li"
			testId={label}
			automationHook="recommended-product-option"
			quantity={newQuantity}
			variant={selectedVariant}
			variantChange={onVariantSelected}
			quantityChange={setNewQuantity}
			displayModelNumber={true}>
			<ProductCardActions
				isBusy={isAddToCartBusy}
				className="items-center justify-end mt3"
				link={{
					name: 'See More Details',
					url: selectedVariant.url,
					automationHook: 'product-option-see-more-details',
					openInNewTab: true
				}}
				action={{
					label: option.hasPricedOptions ? 'Configure' : option.selected ? 'Add More' : 'Add To Cart',
					automationHook: 'product-option-add-to-cart',
					onConfigureClick,
					onClick: onAddToCartClick,
					isConfigurable: option.hasPricedOptions,
					isThisTheProductBeingAddedToCart
				}}
				serverAnalytics={
					source === 'DYNAMIC_YIELD'
						? {
								mutation: REPORT_RECOMMENDATION_ENGAGEMENT,
								variables: {
									slotId
								}
						  }
						: undefined
				}
			/>
		</ProductSelectionCard>
	) : null;
};

export type ProductOptionRecommendedListProps = {
	selections: OptionSelection[];
	itemClassName?: string;
	addToCart?: AddToCartAction;
	variantName?: string;
	isAddToCartBusy?: boolean;
	numOptionsToShow?: number;
} & ProductOptionGroupFieldsFragment &
	Pick<HTMLAttributes<HTMLElement>, 'className'>;

/**
 * Product Option Recommended List Component
 */
export const ProductOptionRecommendedList: FunctionComponent<ProductOptionRecommendedListProps> = ({
	className = 'w-100 pa0',
	itemClassName = '',
	selections,
	addToCart,
	variantName,
	isAddToCartBusy,
	numOptionsToShow = 10,
	...group
}) => {
	const [recommendedOptions] = useState(group);
	const [numRecOpsDisplayed, setNumRecOpsDisplayed] = useState(numOptionsToShow);
	const { label, options } = recommendedOptions;
	const groupId = kebabCase(label);

	const handleViewMoreClick = () => {
		setNumRecOpsDisplayed(numRecOpsDisplayed + GROUP_SIZE);
	};

	const optionsToDisplay = options.slice(0, numRecOpsDisplayed);

	return (
		<div>
			<ul className={`list flex flex-column items-start justify-start ma0 ${className}`.trim()} data-testid={groupId}>
				{optionsToDisplay.map((option) => {
					return (
						<ProductOptionRecommended
							{...option}
							key={kebabCase(`${groupId}-${option.name}-${option.resource?.variantId}`)}
							label={label}
							selected={isCurrentOptionSelected(option, selections)}
							addToCart={addToCart}
							variantName={variantName}
							className={`pa3 mb3 ${itemClassName}`}
							source={group.source}
							isAddToCartBusy={isAddToCartBusy}
						/>
					);
				})}
			</ul>
			{numRecOpsDisplayed < options.length && (
				<div className="flex justify-center pb4">
					<StyledButton buttonStyle="SECONDARY" onClick={handleViewMoreClick} testId={`${groupId}-view-more`}>
						View More
					</StyledButton>
				</div>
			)}
		</div>
	);
};
